import { useCallback, useEffect, useState } from "react";

import type { AnswerValue, AnswersDraft } from "@/features/respond/data/types";

/**
 * useDraftAnswers — 回答 draft を localStorage に永続化する hook。
 *
 * タブを閉じても・ブラウザを再起動しても同一端末であれば回答の続きから再開できるよう
 * localStorage に保存する (ADR-0016 Phase 1)。key は invitationId 単位で分離し、
 * 別の招待を踏んだときに前の回答が混ざらないようにしている。
 *
 * 回答送信成功時に clearDraft() で削除する (呼び出しは SurveyForm 側)。
 * 端末をまたいだ再開はサポートしない。必要になれば Phase 2 としてサーバ側保存を検討する。
 */

const STORAGE_PREFIX = "wc.respond.draft.";

function storageKey(invitationId: string): string {
  return `${STORAGE_PREFIX}${invitationId}`;
}

function loadInitial(invitationId: string): AnswersDraft {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey(invitationId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as AnswersDraft;
    }
    return {};
  } catch {
    return {};
  }
}

export interface DraftAnswersApi {
  answers: AnswersDraft;
  setAnswer: (questionId: string, value: AnswerValue) => void;
  // QA 自動入力 (QaAutofillPanel) が生成した回答一式をまとめて流し込むための口。
  // 既存の draft は置き換える (1 問ずつ setAnswer するより意図が明確なため専用 API にする)。
  setAnswers: (next: AnswersDraft) => void;
  clearDraft: () => void;
}

export function useDraftAnswers(invitationId: string): DraftAnswersApi {
  const [answers, setAnswers] = useState<AnswersDraft>(() => loadInitial(invitationId));

  // invitationId が変わるとロードし直す (通常起きないが、router 上の再利用に備える)
  useEffect(() => {
    setAnswers(loadInitial(invitationId));
  }, [invitationId]);

  useEffect(() => {
    try {
      const key = storageKey(invitationId);
      // 空 object は「初期状態 or clearDraft 済み」を表すので key ごと消す。
      // setItem してしまうと clearDraft 直後にこの effect が発火したとき、
      // 削除済みの draft を空 object で復活させてしまう (ADR-0016「回答完了で削除」)。
      if (Object.keys(answers).length === 0) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(answers));
      }
    } catch {
      // quota 超過などは静かに無視。リロード耐性が失われるだけで送信フローは継続できる
    }
  }, [invitationId, answers]);

  const setAnswer = useCallback((questionId: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const setAllAnswers = useCallback((next: AnswersDraft) => {
    setAnswers(next);
  }, []);

  const clearDraft = useCallback(() => {
    setAnswers({});
  }, []);

  return { answers, setAnswer, setAnswers: setAllAnswers, clearDraft };
}
